import { useReactiveVar } from '@apollo/client'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { Typography } from 'antd'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import clsx from 'clsx'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import { FamilyNumberFormat } from 'models/projectSettings'
import { handleViewNotes } from 'pages/ProjectsPage/WorkspacePage/components/ProjectNotesDialog/notesUtils'
import { getFormattedFamilyNumber } from 'pages/ProjectsPage/projectUtils'
import React from 'react'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { getDialogUrl, getElementType } from 'shared/utils/workspaceUtils'
import TaskIndicatorButton from './TaskIndicatorButton'
import { useElementItemShallowSelector } from '../ElementItemStore'
import { useProjectSettings } from '../Workspace2Store'

const useStyles = makeStyles((theme: Theme) => ({
    header: {
        minHeight: pxToRem(50),
        padding: theme.spacing(1, 4),
        '@media (max-width: 1280px)': {
            flexWrap: 'wrap',
        },
        justifyContent: 'center',
    },
    elementTypeGridItemForTopic: {
        maxHeight: pxToRem(15),
        paddingRight: theme.spacing(1.25),
    },
    headerButtons: {
        display: 'flex',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        alignItems: 'center',
        justifyContent: 'flex-end',
        margin: 'auto',
        columnGap: pxToRem(10),
        '@media (max-width: 1600px)': {
            flexWrap: 'wrap',
            minWidth: pxToRem(225),
        },
        '@media (max-width: 1280px)': {
            paddingTop: theme.spacing(1),
        },
    },
    elementTitle: {
        display: 'flex',
        alignItems: 'center',
        textAlign: 'center',
        '& .elementTitleContainer': {
            '@media (max-width: 1100px)': {
                flexWrap: 'wrap',
            },
        },
    },
    elementNumber: {
        fontSize: '18px',
        fontFamily: 'Roboto',
        fontWeight: 600,
        lineHeight: '21.09px',
        color: '#314959',
        display: 'inline',
    },
    elementText: {
        fontSize: '18px',
        fontFamily: 'Roboto',
        fontWeight: 600,
        lineHeight: '21.09px',
        color: '#314959',
        display: 'inline',
    },
    elementType: {
        fontSize: pxToRem(14),
        lineHeight: pxToRem(16.41),
        fontWeight: 500,
        textTransform: 'capitalize',
    },
    drawingTagBox: {
        display: 'flex',
        flex: '1',
        minWidth: pxToRem(225),
    },
    notesText: {
        color: '#13B5EA',
        fontWeight: 600,
        fontSize: pxToRem(13),
        lineHeight: pxToRem(16.41),
    },
    elementTypeGridItem: {
        maxHeight: pxToRem(15),
        borderRight: '1px solid black',
        paddingRight: theme.spacing(1.25),
    },
    notesGridItem: {
        paddingLeft: theme.spacing(1),
        cursor: 'pointer',
    },
    roundContainer: {
        width: '122px',
        height: '38px',
        backgroundColor: '#314959',
        borderRadius: '20px',
        flexShrink: 0,
        display: 'flex',
        position: 'relative',
        right: '15px',
    },
    roundContainerActionBar: {
        width: pxToRem(156),
        height: pxToRem(38),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(0, 1.875),
        backgroundColor: '#314959',
        borderRadius: '20px',
        flexShrink: 0,
        position: 'relative',
        right: '30px',
    },
    actionBarButton: {
        width: '24px',
        height: '24px',
        backgroundColor: '#314959',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    circleBackground: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        transform: 'translate(-50%, -50%)',
    },
}))

interface WorkspaceTitleProps {
    projectId?: string
    selectedElementFormat?: string
    getPersistedState?: (propKey: string) => any
    contentTeamFromStudioPage?: boolean
    selectedTopic?: Partial<ProductFamilyStudioManagement>
}
const WorkspaceTitle: React.FC<WorkspaceTitleProps> = ({
    projectId = '',
    selectedElementFormat,
    getPersistedState,
    contentTeamFromStudioPage,
    selectedTopic,
}) => {
    const classes = useStyles()

    const projectSettings = useProjectSettings()
    const famNumberFormat =
        projectSettings?.familyNumberFormat ?? FamilyNumberFormat.XXXXXX
    const { selectedElementNode: selectedProjectElementNode, elementLevelNotesCount } = useReactiveVar(
        workspaceSelectedElement
    )
    const { previewFeatures } = usePreviewFeatures()
    const { elementItemValue } = useElementItemShallowSelector((s) => ({
        elementItemValue: s.elementItemValue,
    }))

    const handleOpenTasks = React.useCallback(() => {
        if (!projectId || !selectedProjectElementNode) return

        let family = ''
        let productType = ''
        const elType = selectedProjectElementNode.elementType

        if (elType === 'PRODUCTFAMILY') {
            family = selectedProjectElementNode.text?.split('|').join(' - ') ?? ''
        } else if (elType === 'PRODUCTTYPE') {
            const treeSegments = (selectedProjectElementNode.treePath ?? '').split('||').filter(Boolean)
            const parentSegment = treeSegments[treeSegments.length - 2] ?? ''
            family = parentSegment.split('|').filter(Boolean).join(' - ')
            productType = selectedProjectElementNode.text?.split('|').join(' - ') ?? ''
        }

        const familyId = elType === 'PRODUCTFAMILY'
            ? selectedProjectElementNode.elementId || ''
            : elType === 'PRODUCTTYPE'
                ? elementItemValue.parentFamilyId || ''
                : ''

        const baseUrl = getDialogUrl(
            {
                projectId,
                elementNode: selectedProjectElementNode,
                family,
                productType,
                familyId: familyId || undefined,
            },
            'projecttasks'
        )

        const separator = baseUrl.includes('?') ? '&' : '?'
        const url = `${baseUrl}${separator}noContentItem=true`

        window.open(
            url,
            `ProjectTasks${projectId}`,
            `directories=no,titlebar=no,location=no,toolbar=no,menubar=no,resizable=yes,height=${window.screen.height},width=${window.screen.width}`
        )
    }, [projectId, selectedProjectElementNode, elementItemValue])

    const elementType = React.useMemo(() => {
        if (selectedTopic) {
            return 'Sustainability Topic'
        } else {
            if (
                selectedElementFormat === 'DIVISIONS' &&
                selectedProjectElementNode?.elementType !== 'PRODUCTTYPE'
            ) {
                return selectedProjectElementNode?.elementType === 'ASSEMBLY'
                    ? 'Division'
                    : 'Section'
            } else {
                if (
                    contentTeamFromStudioPage &&
                    selectedProjectElementNode?.elementType !== 'ASSEMBLY'
                ) {
                    return 'Family'
                } else {
                    return getElementType(
                        selectedProjectElementNode?.elementType ?? ''
                    )
                }
            }
        }
    }, [
        selectedElementFormat,
        selectedProjectElementNode,
        selectedTopic,
        contentTeamFromStudioPage,
    ])

    const formattedFamNumber = React.useMemo(() => {
        const familyName = contentTeamFromStudioPage
            ? selectedProjectElementNode?.parentTreePath?.split('||')?.[
                  selectedProjectElementNode?.parentTreePath?.split('||')
                      ?.length - 1
              ]
            : null
        if (
            contentTeamFromStudioPage &&
            selectedProjectElementNode?.elementType === 'PRODUCTTYPE'
        ) {
            return getFormattedFamilyNumber(
                familyName?.split('|')?.[0] ?? '',
                famNumberFormat
            )
        } else {
            return elementType !== 'Family'
                ? selectedProjectElementNode?.text.split('|')[0] ?? ''
                : getFormattedFamilyNumber(
                      selectedProjectElementNode?.text.split('|')[0] ?? '',
                      famNumberFormat
                  )
        }
    }, [
        elementType,
        famNumberFormat,
        selectedProjectElementNode,
        getFormattedFamilyNumber,
    ])
    const formattedFamilyName = React.useMemo(() => {
        if (
            contentTeamFromStudioPage &&
            selectedProjectElementNode?.elementType === 'PRODUCTTYPE'
        ) {
            const familyName = contentTeamFromStudioPage
                ? selectedProjectElementNode?.parentTreePath?.split('||')?.[
                      selectedProjectElementNode?.parentTreePath?.split('||')
                          ?.length - 1
                  ]
                : null
            return familyName?.split('|')?.[1]
        } else return selectedProjectElementNode?.text.split('|')[1] ?? ''
    }, [selectedProjectElementNode, contentTeamFromStudioPage])

    const callViewNotes = React.useCallback(() => {
        handleViewNotes({
            projectId: projectId ?? '',
            getPersistedState: getPersistedState,
            selectedProjectElementNode: selectedProjectElementNode,
        })
    }, [projectId, selectedProjectElementNode, getPersistedState])

    return (
        <>
            <GridContainer
                item
                wrap="nowrap"
                className={clsx(classes.header, 'header')}
            >
                <GridItem
                    className={classes.elementTitle}
                    data-testid="elementTitle"
                >
                    <GridContainer
                        className="elementTitleContainer"
                        wrap="nowrap"
                    >
                        {contentTeamFromStudioPage && selectedTopic ? (
                            <GridItem>
                                <Typography className={classes.elementText}>
                                    {`${selectedTopic?.familyNumber ?? ''} ${
                                        selectedTopic?.name ?? ''
                                    }`}
                                </Typography>
                                <GridItem
                                    className={
                                        classes.elementTypeGridItemForTopic
                                    }
                                >
                                    <Typography className={classes.elementType}>
                                        Sustainability Topic
                                    </Typography>
                                </GridItem>
                            </GridItem>
                        ) : (
                            <GridItem style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                {previewFeatures.hasProjectTasks && elementLevelNotesCount > 0 && (
                                    <TaskIndicatorButton onClick={handleOpenTasks} />
                                )}
                                <span>
                                    <Typography className={classes.elementNumber}>
                                        {selectedProjectElementNode
                                            ? `${formattedFamNumber} `
                                            : ''}
                                    </Typography>
                                    {formattedFamNumber && formattedFamilyName && (
                                        <Typography className={classes.elementText}>
                                            {' - '}
                                        </Typography>
                                    )}
                                    <Typography className={classes.elementText}>
                                        {selectedProjectElementNode
                                            ? `${formattedFamilyName}`
                                            : ''}
                                    </Typography>
                                </span>
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </>
    )
}

export default WorkspaceTitle
